import { logger } from '@/core/logger';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { WebView } from 'react-native-webview';

export interface UsePaymentCheckoutReturn {
    checkoutUrl: string | null;
    orderRef: string | null;
    isLoading: boolean;
    error: Error | null;
    webViewRef: React.RefObject<WebView | null>;
    handleBackPress: () => void;
    handleShouldStartLoadWithRequest: (request: any) => boolean;
    handleLoadStart: () => void;
    handleLoadEnd: () => void;
    handleError: (syntheticEvent: any) => void;
    handleNavigationStateChange: (navState: any) => void;
    handleMessage: (event: any) => void;
}

/**
 * Payment status URLs
 * Payment redirect base URL is environment-specific:
 * - Dev: https://devapp.bloomholding.com/payment-redirect
 * - UAT: https://uatapp.bloomholding.com/payment-redirect
 * - Prod: https://app.bloomholding.com/payment-redirect
 * Status is appended as query parameter: ?status=success|failed|cancelled
 */
const PAYMENT_REDIRECT_BASE_URL = process.env.EXPO_PUBLIC_PAYMENT_REDIRECT_BASE_URL || 'https://devapp.bloomholding.com/payment-redirect';

/**
 * TODO: Remove this constant and old format handling once all environments are migrated to web URLs
 * Old deep link format for backward compatibility (e.g., https://devbloomone-payment-redirect)
 */
const PAYMENT_REDIRECT_OLD_FORMAT = 'devbloomone-payment-redirect';
const TELR_PROCESS_URL_PATTERN = 'https://secure.telr.com/gateway/process.html';

/**
 * Check if URL matches payment redirect pattern (both new web URL and old deep link format)
 * TODO: Remove old format check once all environments are migrated to web URLs
 */
const isPaymentRedirectUrl = (url: string): boolean => {
    // Check new web URL format
    if (url.includes(PAYMENT_REDIRECT_BASE_URL)) {
        return true;
    }
    // Check old deep link format for backward compatibility
    // TODO: Remove this check once all environments are migrated to web URLs
    if (url.includes(PAYMENT_REDIRECT_OLD_FORMAT)) {
        return true;
    }
    return false;
};

/**
 * Hook for payment checkout screen
 * Handles WebView logic and navigation
 */
export function usePaymentCheckout(): UsePaymentCheckoutReturn {
    const params = useLocalSearchParams<{ checkoutUrl?: string; orderRef?: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const webViewRef = useRef<WebView>(null);
    const processedPaymentStatusRef = useRef(false);
    const currentUrlRef = useRef<string | null>(null);

    const checkoutUrl = params.checkoutUrl || null;
    const orderRef = params.orderRef || null;

    useEffect(() => {
        if (!checkoutUrl) {
            const error = new Error('No checkout URL provided. Please try again or contact support.');
            logger.error('usePaymentCheckout:No checkout URL provided', { error: String(error) });
            setError(error);
        } else {
            // Clear error if checkoutUrl is available
            setError(null);
        }
    }, [checkoutUrl]);

    /**
     * Navigate to payment redirect screen with status and orderRef
     */
    const navigateToRedirect = useCallback((status: 'success' | 'failed' | 'cancelled') => {
        if (processedPaymentStatusRef.current) {
            return;
        }
        processedPaymentStatusRef.current = true;
        logger.info(`usePaymentCheckout:Payment ${status}, navigating to redirect screen`);
        
        // Track payment status event
        const actionName = status === 'success' 
            ? 'Payment Success' 
            : status === 'failed' 
                ? 'Payment Failed' 
                : 'Payment Cancelled';
        
        trackDynatraceEvent({
            p_screen: 'Payment Checkout',
            p_actionName: actionName,
            p_sectionName: 'Payment Flow',
            eventType: status === 'success' ? 'action' : 'error',
            paymentStatus: status,
            orderRef: orderRef || '',
        });
        
        const params = new URLSearchParams({
            status,
            ...(orderRef ? { orderRef } : {}),
        });
        router.replace(`/(auth)/payment-redirect?${params.toString()}` as any);
    }, [orderRef]);

    /**
     * Handle navigation state changes to detect payment status
     */
    const handleNavigationStateChange = useCallback((navState: any) => {
        const { url, title } = navState;
        currentUrlRef.current = url;

        logger.debug('usePaymentCheckout:Navigation state changed', { url, title });

        // Check for payment redirect URLs - redirect immediately without loading
        // Supports both new web URL format and old deep link format for backward compatibility
        // TODO: Remove old format handling once all environments are migrated to web URLs
        if (isPaymentRedirectUrl(url)) {
            // Extract status from URL query parameters
            try {
                const urlObj = new URL(url);
                const statusParam = urlObj.searchParams.get('status');
                
                if (statusParam === 'success') {
                    logger.info('usePaymentCheckout:Payment success URL detected', url);
                    navigateToRedirect('success');
                    return;
                } else if (statusParam === 'failed') {
                    logger.info('usePaymentCheckout:Payment failed URL detected', url);
                    navigateToRedirect('failed');
                    return;
                } else if (statusParam === 'cancelled') {
                    logger.info('usePaymentCheckout:Payment cancelled URL detected', url);
                    navigateToRedirect('cancelled');
                    return;
                }
            } catch (error) {
                // Fallback to string matching if URL parsing fails (e.g., old deep links)
                // TODO: Remove this fallback once all environments are migrated to web URLs
                logger.warn('usePaymentCheckout:Failed to parse URL, using fallback matching', error);
                if (url.includes('status=success')) {
                    logger.info('usePaymentCheckout:Payment success URL detected (fallback)', url);
                    navigateToRedirect('success');
                    return;
                } else if (url.includes('status=failed')) {
                    logger.info('usePaymentCheckout:Payment failed URL detected (fallback)', url);
                    navigateToRedirect('failed');
                    return;
                } else if (url.includes('status=cancelled')) {
                    logger.info('usePaymentCheckout:Payment cancelled URL detected (fallback)', url);
                    navigateToRedirect('cancelled');
                    return;
                }
            }
        }

        // Check for expired session by checking page title or URL pattern
        if (url.includes(TELR_PROCESS_URL_PATTERN)) {
            // Check if title indicates expired session
            if (title && (title.toLowerCase().includes('invalid') || title.toLowerCase().includes('expired'))) {
                logger.warn('usePaymentCheckout:Expired payment session detected');
                navigateToRedirect('failed');
                return;
            }
        }
    }, [navigateToRedirect]);

    const handleBackPress = useCallback(() => {
        router.back();
    }, []);

    const handleShouldStartLoadWithRequest = useCallback((request: any) => {
        const { url } = request;
        logger.debug('usePaymentCheckout:Should start load with request', url);

        // Check for payment redirect URLs - don't load, redirect immediately
        // Supports both new web URL format and old deep link format for backward compatibility
        // TODO: Remove old format handling once all environments are migrated to web URLs
        if (isPaymentRedirectUrl(url)) {
            // Extract status from URL query parameters
            try {
                const urlObj = new URL(url);
                const statusParam = urlObj.searchParams.get('status');
                
                if (statusParam === 'success') {
                    logger.info('usePaymentCheckout:Payment success URL detected, redirecting immediately', url);
                    navigateToRedirect('success');
                    return false; // Don't load in WebView
                } else if (statusParam === 'failed') {
                    logger.info('usePaymentCheckout:Payment failed URL detected, redirecting immediately', url);
                    navigateToRedirect('failed');
                    return false; // Don't load in WebView
                } else if (statusParam === 'cancelled') {
                    logger.info('usePaymentCheckout:Payment cancelled URL detected, redirecting immediately', url);
                    navigateToRedirect('cancelled');
                    return false; // Don't load in WebView
                }
            } catch (error) {
                // Fallback to string matching if URL parsing fails (e.g., old deep links)
                // TODO: Remove this fallback once all environments are migrated to web URLs
                logger.warn('usePaymentCheckout:Failed to parse URL, using fallback matching', error);
                if (url.includes('status=success')) {
                    logger.info('usePaymentCheckout:Payment success URL detected, redirecting immediately (fallback)', url);
                    navigateToRedirect('success');
                    return false; // Don't load in WebView
                } else if (url.includes('status=failed')) {
                    logger.info('usePaymentCheckout:Payment failed URL detected, redirecting immediately (fallback)', url);
                    navigateToRedirect('failed');
                    return false; // Don't load in WebView
                } else if (url.includes('status=cancelled')) {
                    logger.info('usePaymentCheckout:Payment cancelled URL detected, redirecting immediately (fallback)', url);
                    navigateToRedirect('cancelled');
                    return false; // Don't load in WebView
                }
            }
        }

        // Allow all other URLs to load
        return true;
    }, [navigateToRedirect]);

    const handleLoadStart = useCallback(() => {
        setIsLoading(true);
    }, []);

    const handleLoadEnd = useCallback(() => {
        setIsLoading(false);

        // Check for expired session by reading page content
        if (currentUrlRef.current && currentUrlRef.current.includes(TELR_PROCESS_URL_PATTERN)) {
            // Inject JavaScript to check for expired session message
            webViewRef.current?.injectJavaScript(`
                (function() {
                    const bodyText = document.body.innerText || document.body.textContent || '';
                    if (bodyText.toLowerCase().includes('invalid payment page session') || 
                        bodyText.toLowerCase().includes('expired') ||
                        bodyText.toLowerCase().includes('already been used')) {
                        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'expired' }));
                    }
                })();
            `);
        }
    }, []);

    /**
     * Handle messages from injected JavaScript
     */
    const handleMessage = useCallback((event: any) => {
        try {
            const data = JSON.parse(event.nativeEvent.data);
            if (data.type === 'expired') {
                logger.warn('usePaymentCheckout:Expired session detected from page content');
                navigateToRedirect('failed');
            }
        } catch {
            // Ignore parse errors
        }
    }, [navigateToRedirect]);

    const handleError = useCallback((syntheticEvent: any) => {
        const { nativeEvent } = syntheticEvent;
        logger.error('usePaymentCheckout:WebView error', nativeEvent);
        setIsLoading(false);
    }, []);

    return {
        checkoutUrl,
        orderRef,
        isLoading,
        error,
        webViewRef,
        handleBackPress,
        handleShouldStartLoadWithRequest,
        handleLoadStart,
        handleLoadEnd,
        handleError,
        handleNavigationStateChange,
        handleMessage,
    };
}

